import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Mail, ArrowLeft, CheckCircle, Copy, ExternalLink } from 'lucide-react';

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await authApi.forgotPassword(email);
      setSent(true);
      if (res.data.reset_token) {
        setResetToken(res.data.reset_token);
      }
      toast.success('Återställningslänk har genererats');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Något gick fel. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  const copyToken = () => {
    if (resetToken) {
      navigator.clipboard.writeText(resetToken);
      toast.success('Token kopierad!');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center py-12 px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-1 mb-6">
            <span className="text-3xl font-black text-[#003B73]">Bygg</span>
            <span className="text-3xl font-black text-[#FF8A00]">Fynd</span>
          </Link>
          <h1 className="text-2xl font-black text-[#0F172A]">Glömt lösenord</h1>
          <p className="text-gray-500 mt-2">
            {sent
              ? 'En återställningslänk har genererats.'
              : 'Ange din e-postadress för att återställa ditt lösenord.'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          {!sent ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="forgot-email">E-postadress</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 h-11 rounded-xl"
                    placeholder="din@email.se"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#003B73] hover:bg-[#002a52] font-bold rounded-xl"
                data-testid="forgot-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Skickar...</>
                ) : (
                  'Skicka återställningslänk'
                )}
              </Button>
            </form>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                <p className="text-sm text-emerald-700">
                  En återställningslänk har skapats för <strong>{email}</strong>.
                </p>
              </div>

              {resetToken && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">Använd denna token för att återställa ditt lösenord:</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs font-mono text-gray-700 truncate" data-testid="reset-token-display">
                      {resetToken}
                    </code>
                    <Button variant="outline" size="sm" onClick={copyToken} className="rounded-lg flex-shrink-0" data-testid="copy-token-btn">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <Link
                    to={`/aterstall-losenord?token=${resetToken}`}
                    className="flex items-center justify-center gap-2 w-full h-11 bg-[#FF8A00] hover:bg-[#e67c00] text-white font-bold rounded-xl transition-colors"
                    data-testid="go-to-reset-btn"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Gå till återställning
                  </Link>
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => { setSent(false); setResetToken(null); setEmail(''); }}
                className="w-full rounded-xl"
                data-testid="try-again-btn"
              >
                Försök med en annan e-postadress
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/logga-in"
              className="inline-flex items-center gap-1 text-sm text-[#003B73] hover:underline font-medium"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="h-4 w-4" />
              Tillbaka till inloggning
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
